import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import Button from '../../util_components/button';
import CustomInput from '../../util_components/custom_input';
import CustomLoading from '../../util_components/custom_loading';
import SearchableSelect from '../reminders/SearchableSelect';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';

const YES_NO = [
  { value: 'Yes', label: 'Yes', color: '#22c55e' },
  { value: 'No', label: 'No', color: '#ef4444' }
];
const YES_NO_NA = [
  { value: 'Yes', label: 'Yes', color: '#22c55e' },
  { value: 'No', label: 'No', color: '#ef4444' },
  { value: 'Not Applicable', label: 'N/A', color: '#9ca3af' }
];
const STATUS_OPTS = [
  { value: 'Red', label: 'Red', color: '#ef4444' },
  { value: 'Yellow', label: 'Yellow', color: '#f59e0b' },
  { value: 'Green', label: 'Green', color: '#22c55e' }
];
const STRENGTH_OPTS = [
  { value: 'Strong', label: 'Strong', color: '#22c55e' },
  { value: 'Weak', label: 'Weak', color: '#ef4444' }
];

// Small round icon button. Explicit background/border overrides the host CMS's
// global orange `button {}` rule (which otherwise renders these as orange blobs).
const ICON_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 28,
  width: 28,
  borderRadius: 9999,
  border: '1px solid #d1d5db',
  background: 'var(--surface-card)',
  cursor: 'pointer',
  padding: 0
};

// Per-checkpoint checklist form. The field list, which fields are read-only
// (blue/auto), and which are hidden (red/not-applicable) all come from the
// server schema — this component just renders widgets by shape.
class ChecklistForm extends Component {
  state = {
    loading: true,
    saving: false,
    meta: {},
    fields: [],
    reply: {},
    auto_data: {},
    teacher_options: []
  };

  componentDidMount() {
    this.load_form();
    this.load_teachers();
  }

  // Teacher list backs the type-to-filter selector on teacher fields.
  load_teachers = () => {
    const url = get_api_url('get_teachers_list');
    post_api(url, null, true)
      .then((res) => {
        const teachers = (res.data.teachers || [])
          .filter((t) => t.onboarding_status === 'APPROVED')
          .map((t) => ({
            value: t.uuid,
            label: `${t.first_name || ''} ${t.last_name || ''}`.trim()
          }));
        teachers.sort((a, b) => a.label.localeCompare(b.label));
        this.setState({ teacher_options: teachers });
      })
      .catch(() => {
        // Non-fatal: the selector just shows no options if this fails.
      });
  };

  load_form = () => {
    this.setState({ loading: true });
    const url = get_api_url(`/v2/early_retention_checklist/form/${this.props.uuid}`);
    get_api(url, true)
      .then((res) => {
        const d = res.data.data;
        this.setState({
          loading: false,
          fields: d.fields || [],
          reply: d.reply || {},
          auto_data: d.auto_data || {},
          meta: {
            student_name: d.student_name,
            check_label: d.check_label,
            checkin_date: d.checkin_date,
            day_number: d.day_number,
            week_number: d.week_number,
            pause_days_added: d.pause_days_added,
            status: d.status,
            submitted_by_name: d.submitted_by_name
          }
        });
      })
      .catch((e) => {
        let err = 'Failed to load form';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ loading: false });
      });
  };

  // ── value setters ───────────────────────────────────────────────────────
  set_value = (key, value) => this.setState((prev) => ({ reply: { ...prev.reply, [key]: value } }));

  set_obj_value = (key, subkey, value) =>
    this.setState((prev) => ({
      reply: { ...prev.reply, [key]: { ...(prev.reply[key] || {}), [subkey]: value } }
    }));

  // ── teacher picker list (Main Teacher Found) ──────────────────────────────
  // Value shape: { value: 'Yes'|'No', teachers: [{ uuid, name }] }
  teacher_rows = (key) => {
    const obj = this.state.reply[key] || {};
    const list = Array.isArray(obj.teachers) ? obj.teachers : [];
    return list.length ? list : [{ uuid: '', name: '' }];
  };

  set_teacher = (key, idx, uuid) => {
    const opt = this.state.teacher_options.find((o) => o.value === uuid);
    const rows = this.teacher_rows(key).map((t, i) =>
      i === idx ? { uuid, name: opt ? opt.label : '' } : t
    );
    this.set_obj_value(key, 'teachers', rows);
  };

  add_teacher = (key) =>
    this.set_obj_value(key, 'teachers', [...this.teacher_rows(key), { uuid: '', name: '' }]);

  remove_teacher = (key, idx) => {
    const rows = this.teacher_rows(key).filter((_, i) => i !== idx);
    this.set_obj_value(key, 'teachers', rows);
  };

  // ── teacher relationship list ─────────────────────────────────────────────
  rel_list = (key) => (Array.isArray(this.state.reply[key]) ? this.state.reply[key] : []);

  add_rel = (key) =>
    this.set_value(key, [...this.rel_list(key), { teacher_name: '', strength: '' }]);

  update_rel = (key, idx, subkey, value) => {
    const list = this.rel_list(key).map((r, i) => (i === idx ? { ...r, [subkey]: value } : r));
    this.set_value(key, list);
  };

  // Set the teacher on a relationship row from the type-to-filter selector,
  // storing both the uuid and resolved display name.
  set_rel_teacher = (key, idx, uuid) => {
    const opt = this.state.teacher_options.find((o) => o.value === uuid);
    const list = this.rel_list(key).map((r, i) =>
      i === idx ? { ...r, uuid, teacher_name: opt ? opt.label : '' } : r
    );
    this.set_value(key, list);
  };

  remove_rel = (key, idx) =>
    this.set_value(
      key,
      this.rel_list(key).filter((_, i) => i !== idx)
    );

  // ── persistence ───────────────────────────────────────────────────────────
  save_draft = () => this.persist('/v2/early_retention_checklist/save_draft', false);

  submit = () => this.persist('/v2/early_retention_checklist/submit', true);

  persist = (path, is_submit) => {
    this.setState({ saving: true });
    const url = get_api_url(path);
    post_api(url, { uuid: this.props.uuid, reply: this.state.reply }, true)
      .then(() => {
        this.setState({ saving: false });
        if (is_submit) {
          this.props.set_notification_variable(true, 'success', 'Checklist submitted');
          this.props.on_submitted();
        } else {
          this.props.set_notification_variable(true, 'success', 'Draft saved');
          this.props.on_saved_draft();
        }
      })
      .catch((e) => {
        let err = is_submit ? 'Failed to submit' : 'Failed to save draft';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.props.set_notification_variable(true, 'error', err);
        this.setState({ saving: false });
      });
  };

  // ── field renderers ─────────────────────────────────────────────────────────
  // Segmented toggle — one click to pick, no dropdown. Used for Yes / No / N/A
  // and the Overall Status (Red / Yellow / Green). The selected option fills
  // with its colour (opt.color, falling back to brand blue); explicit inline
  // backgrounds override the host CMS's global orange `button {}` rule.
  render_toggle = (value, options, onChange) => (
    <div className="inline-flex rounded-md overflow-hidden" style={{ border: '1px solid #d1d5db' }}>
      {options.map((opt, idx) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              padding: '6px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              borderLeft: idx === 0 ? 'none' : '1px solid #d1d5db',
              background: selected ? opt.color || '#2563eb' : 'var(--surface-card)',
              color: selected ? '#ffffff' : 'var(--text-muted)'
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  render_text_input = (field, type = 'text') => {
    const val = this.state.reply[field.key] ?? '';
    return (
      <CustomInput
        show_label={false}
        type={type}
        name={field.key}
        value={val}
        style={{ width: '220px' }}
        input_style={{ width: '100%' }}
        onChange={(e) => this.set_value(field.key, e.target.value)}
      />
    );
  };

  render_manual_field = (field) => {
    switch (field.shape) {
      case 'yes_no':
        return this.render_toggle(this.state.reply[field.key] || '', YES_NO, (v) =>
          this.set_value(field.key, v)
        );
      case 'yes_no_na':
        return this.render_toggle(this.state.reply[field.key] || '', YES_NO_NA, (v) =>
          this.set_value(field.key, v)
        );
      case 'status':
        return this.render_toggle(this.state.reply[field.key] || '', STATUS_OPTS, (v) =>
          this.set_value(field.key, v)
        );
      case 'count':
        return this.render_text_input(field, 'number');
      case 'date':
        return this.render_text_input(field, 'date');
      case 'text':
        return (
          <textarea
            name={field.key}
            value={this.state.reply[field.key] ?? ''}
            rows={8}
            className="w-full border rounded px-2 py-1 text-sm"
            style={{
              borderColor: 'var(--input-border)',
              background: 'var(--input-bg)',
              color: 'var(--input-text)',
              minHeight: '160px',
              resize: 'vertical'
            }}
            onChange={(e) => this.set_value(field.key, e.target.value)}
          />
        );
      case 'yes_no_teacher': {
        const obj = this.state.reply[field.key] || {};
        const rows = this.teacher_rows(field.key);
        return (
          <div className="flex flex-col gap-2 items-start">
            {this.render_toggle(obj.value || '', YES_NO, (v) =>
              this.set_obj_value(field.key, 'value', v)
            )}
            {rows.map((t, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="flex items-center gap-2" key={idx}>
                <div style={{ width: '240px' }}>
                  <SearchableSelect
                    value={t.uuid || ''}
                    options={this.state.teacher_options}
                    placeholder="Search teacher..."
                    clearable
                    onChange={(val) => this.set_teacher(field.key, idx, val)}
                  />
                </div>
                {idx === rows.length - 1 ? (
                  <button
                    type="button"
                    aria-label="Add another teacher"
                    onClick={() => this.add_teacher(field.key)}
                    style={ICON_BTN_STYLE}
                  >
                    <FaPlus size={11} color="#2563eb" />
                  </button>
                ) : null}
                {rows.length > 1 ? (
                  <button
                    type="button"
                    aria-label="Remove teacher"
                    onClick={() => this.remove_teacher(field.key, idx)}
                    style={ICON_BTN_STYLE}
                  >
                    <FaTimes size={11} color="#6b7280" />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        );
      }
      case 'group_classes_found': {
        const obj = this.state.reply[field.key] || {};
        return (
          <div className="flex flex-col gap-2 items-start">
            {this.render_toggle(obj.value || '', YES_NO_NA, (v) =>
              this.set_obj_value(field.key, 'value', v)
            )}
            <div className="flex gap-3 items-center flex-wrap">
              <CustomInput
                show_label={false}
                name={`${field.key}_c1`}
                value={obj.class_1 || ''}
                style={{ width: '200px' }}
                input_style={{ width: '100%' }}
                onChange={(e) => this.set_obj_value(field.key, 'class_1', e.target.value)}
              />
              <CustomInput
                show_label={false}
                name={`${field.key}_c2`}
                value={obj.class_2 || ''}
                style={{ width: '200px' }}
                input_style={{ width: '100%' }}
                onChange={(e) => this.set_obj_value(field.key, 'class_2', e.target.value)}
              />
            </div>
          </div>
        );
      }
      case 'teacher_relationships':
        return (
          <div className="flex flex-col gap-2">
            {this.rel_list(field.key).map((rel, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="flex gap-2 items-center" key={idx}>
                <div style={{ width: '220px' }}>
                  <SearchableSelect
                    value={rel.uuid || ''}
                    options={this.state.teacher_options}
                    placeholder="Search teacher..."
                    clearable
                    onChange={(val) => this.set_rel_teacher(field.key, idx, val)}
                  />
                </div>
                {this.render_toggle(rel.strength || '', STRENGTH_OPTS, (v) =>
                  this.update_rel(field.key, idx, 'strength', v)
                )}
                <button
                  type="button"
                  aria-label="Remove teacher"
                  onClick={() => this.remove_rel(field.key, idx)}
                  style={ICON_BTN_STYLE}
                >
                  <FaTrash size={11} color="#ef4444" />
                </button>
              </div>
            ))}
            <div>
              <Button
                type="secondary"
                custom_style={{ padding: '2px 10px', fontSize: '12px' }}
                onClick={() => this.add_rel(field.key)}
              >
                + Add teacher
              </Button>
            </div>
          </div>
        );
      default:
        return this.render_text_input(field);
    }
  };

  // Format a stored value for read-only display (auto fields, or any field when
  // the checklist is submitted).
  format_field_value = (field, value) => {
    if (value === undefined || value === null || value === '') return '—';
    switch (field.shape) {
      case 'sessions_hours':
        return `${value.sessions ?? 0} sessions, ${value.hours ?? 0} hrs`;
      case 'yes_no_teacher': {
        const names = Array.isArray(value.teachers)
          ? value.teachers.map((t) => t.name || t.teacher_name).filter(Boolean)
          : [];
        return `${value.value || '—'}${names.length ? ` — ${names.join(', ')}` : ''}`;
      }
      case 'group_classes_found': {
        const classes = [value.class_1, value.class_2].filter(Boolean);
        return `${value.value || '—'}${classes.length ? ` — ${classes.join(', ')}` : ''}`;
      }
      case 'teacher_relationships':
        return Array.isArray(value) && value.length
          ? value.map((r) => `${r.teacher_name || '—'} (${r.strength || '—'})`).join(', ')
          : '—';
      default:
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
  };

  render_readonly_field = (field) => {
    const isAuto = field.read_only;
    const raw = isAuto ? this.state.auto_data[field.key] : this.state.reply[field.key];
    // Auto (blue) values are server-computed and stubbed this round.
    if (isAuto && (raw === undefined || raw === null || raw === '')) {
      return (
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Auto-populated (coming soon)
        </span>
      );
    }
    return (
      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
        {this.format_field_value(field, raw)}
      </span>
    );
  };

  render_field = (field) => {
    const readOnly = this.props.read_only || this.state.meta.status === 'submitted';
    const showValue = field.read_only || readOnly;
    return (
      <div
        className="flex items-start gap-4 py-2 border-b"
        style={{ borderColor: 'var(--border-default)' }}
        key={field.key}
      >
        <div className="w-1/2 text-sm" style={{ color: 'var(--text-primary)' }}>
          {field.label}
          {field.read_only ? (
            <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              (auto)
            </span>
          ) : null}
        </div>
        <div className="w-1/2">
          {showValue ? this.render_readonly_field(field) : this.render_manual_field(field)}
        </div>
      </div>
    );
  };

  render_meta = (label, value) => (
    <div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );

  render() {
    const { loading, saving, meta, fields } = this.state;
    const read_only = this.props.read_only || meta.status === 'submitted';

    if (loading) {
      return <CustomLoading />;
    }

    const actual_day = meta.week_number > 0 ? `Week ${meta.week_number}` : `Day ${meta.day_number}`;
    const actual_checkin = meta.checkin_date ? `${meta.checkin_date} (${actual_day})` : actual_day;

    return (
      <div>
        <div className="text-xl font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
          {meta.student_name}
        </div>
        <div className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
          {meta.check_label}
        </div>

        {/* Read-only row metadata (auto-populated). */}
        <div
          className="rounded-md border px-4 py-3 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2"
          style={{ borderColor: 'var(--border-default)', background: 'var(--surface-page)' }}
        >
          {this.render_meta('Days Added to Checkin Date due to Pause', meta.pause_days_added || 0)}
          {this.render_meta('Actual Day of Checkin', actual_checkin)}
          {this.render_meta('Checklist filled by', meta.submitted_by_name || '—')}
        </div>

        <div className="max-h-[60vh] overflow-y-auto pr-2">{fields.map(this.render_field)}</div>

        {read_only ? (
          <div className="mt-6 text-sm" style={{ color: 'var(--text-muted)' }}>
            This checklist has been submitted and is read-only.
          </div>
        ) : (
          <div className="mt-6 flex justify-end gap-3">
            <Button type="secondary" disabled={saving} onClick={this.save_draft}>
              Save to draft
            </Button>
            <Button disabled={saving} onClick={this.submit}>
              {saving ? 'Saving...' : 'Submit'}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

ChecklistForm.propTypes = {
  uuid: PropTypes.string.isRequired,
  read_only: PropTypes.bool,
  close: PropTypes.func.isRequired,
  on_submitted: PropTypes.func.isRequired,
  on_saved_draft: PropTypes.func.isRequired,
  set_notification_variable: PropTypes.func.isRequired
};

ChecklistForm.defaultProps = {
  read_only: false
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ChecklistForm);
